import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { Video, Calendar, Plus, Users, Clock } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';

interface LiveSession {
  id: string;
  title: string;
  description: string;
  scheduled_at: string;
  duration_minutes: number;
  status: string;
  twilio_room_sid: string | null;
  courses: {
    title: string;
  };
}

export default function LiveSessionsPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile?.role === 'docente') {
      fetchTeacherSessions();
    } else if (profile?.role === 'studente') {
      fetchStudentSessions();
    }
  }, [profile]);

  const fetchTeacherSessions = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('live_sessions')
      .select(`
        *,
        courses:course_id(title)
      `)
      .eq('teacher_id', profile?.id)
      .order('scheduled_at', { ascending: true });

    if (data) {
      setSessions(data);
    }
    setLoading(false);
  };

  const fetchStudentSessions = async () => {
    setLoading(true);

    const { data: enrollments } = await supabase
      .from('enrollments')
      .select('course_id')
      .eq('student_id', profile?.id);

    if (enrollments && enrollments.length > 0) {
      const courseIds = enrollments.map(e => e.course_id);

      const { data, error } = await supabase
        .from('live_sessions')
        .select(`
          *,
          courses:course_id(title)
        `)
        .in('course_id', courseIds)
        .order('scheduled_at', { ascending: true });

      if (data) {
        setSessions(data);
      }
    }

    setLoading(false);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live':
        return <Badge className="bg-red-600">In diretta</Badge>;
      case 'scheduled':
        return <Badge variant="secondary">Programmata</Badge>;
      case 'ended':
        return <Badge variant="outline">Terminata</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const isDocente = profile?.role === 'docente';

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Live Sessions
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {isDocente ? 'Gestisci le tue live sessions' : 'Partecipa alle live sessions'}
            </p>
          </div>
          {isDocente && (
            <Link to="/dashboard/live/create">
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Crea live session
              </Button>
            </Link>
          )}
        </div>

        {loading ? (
          <div className="grid gap-6 md:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Video className="w-12 h-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nessuna live session
              </h3>
              <p className="text-gray-600 text-center mb-4">
                {isDocente
                  ? 'Crea la tua prima live session'
                  : 'Non ci sono live sessions disponibili'}
              </p>
              {isDocente && (
                <Link to="/dashboard/live/create">
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Crea live session
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {sessions.map((session) => (
              <Card key={session.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <CardTitle className="text-lg line-clamp-2">
                      {session.title}
                    </CardTitle>
                    {getStatusBadge(session.status)}
                  </div>
                  <CardDescription className="line-clamp-2">
                    {session.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="w-4 h-4 mr-2" />
                      {new Date(session.scheduled_at).toLocaleString('it-IT')}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock className="w-4 h-4 mr-2" />
                      {session.duration_minutes} minuti
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Video className="w-4 h-4 mr-2" />
                      {session.courses?.title}
                    </div>
                    <Link to={`/dashboard/live/${session.id}`}>
                      <Button className="w-full" variant={session.status === 'live' ? 'default' : 'outline'}>
                        {session.status === 'live' ? (
                          <>
                            <Video className="w-4 h-4 mr-2" />
                            Partecipa ora
                          </>
                        ) : session.status === 'scheduled' ? (
                          'Vedi dettagli'
                        ) : (
                          'Vedi registrazione'
                        )}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
